import policyCompany from "./policy.company.mjs";

export const reservationPolicy = {
  id: "reservation-policy",
  route: "/reservation-policy",
  title: "Reservation Terms & Conditions",
  subtitle: "Reservation Policy",
  fileName: "DCAR Reservation Policy.pdf",
  version: "1.0",
  effectiveDate: "4 September 2026",
  company: policyCompany,
  summary:
    "What your RM5,000 reservation covers, how long it lasts, and exactly when it is refunded or forfeited.",
  intro: [
    `This Reservation Policy ("Policy") applies to every vehicle reservation placed through the ${policyCompany.brand} platform at ${policyCompany.website} and the ${policyCompany.brand} mobile experience, operated by ${policyCompany.legalName} (Company No. ${policyCompany.registrationNo}) ("${policyCompany.brand}", "we", "us").`,
    "By placing a reservation and paying the reservation fee, you confirm that you have read, understood and accepted this Policy. Please read the refundable and non-refundable sections carefully before you pay.",
  ],
  sections: [
    {
      heading: "1. What a reservation is",
      paragraphs: [
        "A reservation places a temporary hold on a specific vehicle listing so that it is not offered to other buyers while you complete your checks, financing and documentation.",
      ],
      bullets: [
        {
          label: "It is not a sale",
          text: "A reservation does not create a binding contract to buy or sell the vehicle. The sale is concluded only when a Vehicle Sales Order (VSO) and, where applicable, a Sale and Purchase Agreement are signed by both parties.",
        },
        {
          label: "It is subject to owner confirmation",
          text: "Most vehicles on the platform are held on consignment. DCAR does not own the vehicle unless the listing states otherwise, and a reservation remains subject to confirmation by the registered owner.",
        },
        {
          label: "It is vehicle-specific",
          text: "A reservation applies only to the vehicle listing you selected and cannot be transferred to another vehicle or to another person without our written consent.",
        },
      ],
    },
    {
      heading: "2. Reservation fee",
      bullets: [
        {
          label: "Amount",
          text: "The reservation fee is RM5,000 per vehicle unless a different amount is displayed on the listing or reservation screen at the time of payment.",
        },
        {
          label: "Payment",
          text: "The fee must be paid in full through the payment channels offered on the platform (online banking, credit or debit card, or e-wallet). Reservations are only confirmed once we receive cleared funds and you receive a reservation confirmation.",
        },
        {
          label: "Set-off against the purchase price",
          text: "If you proceed to purchase the vehicle, the reservation fee is credited towards the agreed selling price. It is not an additional charge.",
        },
        {
          label: "No interest",
          text: "The reservation fee is held as a deposit. No interest or profit is payable on the amount held.",
        },
      ],
    },
    {
      heading: "3. Reservation period",
      bullets: [
        {
          label: "Standard period",
          text: "A reservation is valid for 14 calendar days from the date the reservation fee is received, unless a different period is stated on your reservation confirmation.",
        },
        {
          label: "Extension",
          text: "You may request one extension before the reservation expires. Extensions are granted at our discretion and must be confirmed by us in writing.",
        },
        {
          label: "Expiry",
          text: "If the reservation period lapses without a signed VSO, an approved extension, or a documented delay caused by DCAR or the owner, the reservation ends automatically and the vehicle is returned to the market.",
        },
      ],
    },
    {
      heading: "4. When the reservation fee is refundable",
      paragraphs: [
        "We will refund the reservation fee in full, without deduction, in any of the following situations:",
      ],
      bullets: [
        "The vehicle becomes unavailable for any reason — for example the owner withdraws the listing, the vehicle is sold in error to another buyer, or the vehicle is damaged, stolen or written off before handover.",
        "The registered owner does not confirm or declines the reservation.",
        "We are unable to deliver clear and transferable ownership of the vehicle, or a material encumbrance (such as an undisclosed outstanding loan, blacklist or ownership dispute) prevents completion.",
        "The vehicle's actual condition, mileage, accident history or specification differs materially from the published listing or inspection report, and you notify us within 3 working days of discovering the discrepancy.",
        "Your hire purchase or financing application is rejected by all financiers you applied through, and you provide the rejection notice within the reservation period.",
        "DCAR cancels the reservation for its own operational, compliance or commercial reasons.",
        "You cancel in writing within 24 hours of paying the reservation fee, provided you have not yet signed a Vehicle Sales Order.",
      ],
    },
    {
      heading: "5. When the reservation fee is non-refundable",
      paragraphs: [
        "The reservation fee is forfeited, in whole or in part, where the reservation fails for reasons within your control. This includes:",
      ],
      bullets: [
        "You decide not to proceed with the purchase after the 24-hour cancellation window, for reasons other than those listed in Section 4.",
        "You allow the reservation to expire without signing the Vehicle Sales Order, requesting an extension, or responding to our follow-up.",
        "You do not attend a scheduled viewing, inspection, document signing or handover appointment on two or more occasions without reasonable notice.",
        "You provide inaccurate, incomplete or falsified information or documents (including identification, address, income or financing documents) that prevents the transaction from completing.",
        "You fail to submit the identity, financing or insurance documents we reasonably require within the reservation period.",
        "You fail to pay the balance of the purchase price or any agreed instalment by the due date.",
        "You breach any part of this Policy or the Vehicle Sales Order, or you act in a way that makes completion of the sale impracticable.",
        "You request a change of vehicle, buyer name or financing structure that we have not agreed to in writing, and the original transaction cannot proceed as a result.",
      ],
    },
    {
      heading: "6. Partial refunds and deductions",
      paragraphs: [
        "Where a reservation ends and third-party costs have already been incurred at your request — for example an out-of-area vehicle transfer, a specialist inspection, an insurance quotation fee or a financing processing charge — we may deduct the documented cost from the refund and return the balance to you. We will provide a written breakdown of any deduction.",
      ],
    },
    {
      heading: "7. How to request a refund",
      bullets: [
        {
          label: "Where to submit",
          text: `Email ${policyCompany.email} or contact us at ${policyCompany.phone} with your reservation ID, the vehicle listing, and the reason for the refund.`,
        },
        {
          label: "Processing time",
          text: "Approved refunds are processed within 14 working days from the date we confirm the request. The time for funds to appear in your account depends on your bank, card issuer or e-wallet provider.",
        },
        {
          label: "Refund method",
          text: "Refunds are returned to the original payment method and to the account holder who paid. We do not refund to third-party accounts.",
        },
        {
          label: "Records",
          text: "Please keep your payment receipt and reservation confirmation. We may ask for them before releasing a refund.",
        },
      ],
    },
    {
      heading: "8. Vehicle condition and inspection reports",
      paragraphs: [
        "Inspection reports, condition grades and photographs published on the platform describe the vehicle as at the date of inspection. They are provided to help you make an informed decision and are not a warranty that the vehicle is free from defects.",
        "You are encouraged to inspect the vehicle, or appoint your own inspector, during the reservation period. A reservation does not remove your responsibility to satisfy yourself as to the condition of the vehicle before completing the purchase.",
      ],
    },
    {
      heading: "9. Financing and insurance",
      paragraphs: [
        "Where you apply for hire purchase financing or motor insurance through the platform, the application is assessed by the relevant financier or insurer, not by DCAR. Approval, pricing and terms are decided by those third parties. A rejected application is handled under Section 4, provided the rejection notice is supplied within the reservation period.",
      ],
    },
    {
      heading: "10. Personal data",
      paragraphs: [
        "Information you give us when placing a reservation is handled in accordance with our Privacy Policy and the Personal Data Protection Act 2010. This includes the identity, address and supporting documents required for know-your-customer, financing and ownership transfer purposes.",
      ],
    },
    {
      heading: "11. Changes to this policy",
      paragraphs: [
        "We may update this Policy from time to time. The version in force at the time you paid your reservation fee is the version that applies to that reservation. The current version is always published at dcar.my/reservation-policy.",
      ],
    },
    {
      heading: "12. Governing law and contact",
      paragraphs: [
        "This Policy is governed by the laws of Malaysia and the parties submit to the exclusive jurisdiction of the Malaysian courts.",
        `Questions about a reservation or a refund can be sent to ${policyCompany.email} or ${policyCompany.phone}.`,
      ],
    },
  ],
};

export default reservationPolicy;
